import { useMemo, useState } from "react";
import { Hash, Loader2, MessageSquare, Search, Send, UsersRound, X } from "@fg/icons";
import { useMutation } from "@tanstack/react-query";
import {
    useChannels,
    useDirectChats,
    useConversations,
    useTeams
} from "@/api/hooks";
import { connectApi, connectParityApi, type ChatMessage } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

type Destination =
    | { kind: "channel"; teamId: string; channelId: string; label: string }
    | { kind: "dm"; chatId: string; label: string }
    | { kind: "conversation"; conversationId: string; label: string };

/**
 * Teams-style forward-message dialog. Lists every place the user
 * can post into (channels in their teams, 1:1 DMs they're in, group
 * conversations they belong to) and posts the original body as a
 * quoted block to the chosen destination. The wrapper preserves the
 * original author + send-time so the recipient can tell it's a
 * forward, not the sender's own writing.
 */
export function ForwardMessageDialog({
    message,
    onClose
}: {
    message: ChatMessage;
    onClose: () => void;
}) {
    const token = useAccessToken();
    const teams = useTeams();
    const channels0 = useChannels(teams.data?.[0]?.id ?? null);
    const channels1 = useChannels(teams.data?.[1]?.id ?? null);
    const channels2 = useChannels(teams.data?.[2]?.id ?? null);
    const dms = useDirectChats();
    const groups = useConversations();
    const [filter, setFilter] = useState("");
    const [destination, setDestination] = useState<Destination | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Pool every post-able destination into a single list. We don't
    // try to enumerate every team's channels (that would be N HTTP
    // calls per dialog open) — instead we cover the user's first
    // three teams which matches the rail's typical depth. Power users
    // can refine later by typing the channel name.
    const destinations = useMemo<Destination[]>(() => {
        const list: Destination[] = [];
        const teamLookup = new Map((teams.data ?? []).map((t) => [t.id, t.name] as const));
        const seen = new Set<string>();
        const pushChannels = (rows: typeof channels0.data, teamId: string | null) => {
            if (!rows || !teamId) return;
            const teamName = teamLookup.get(teamId) ?? teamId;
            for (const c of rows) {
                if (seen.has(c.id)) continue;
                seen.add(c.id);
                list.push({
                    kind: "channel",
                    teamId,
                    channelId: c.id,
                    label: `${teamName} · #${c.name}`
                });
            }
        };
        pushChannels(channels0.data, teams.data?.[0]?.id ?? null);
        pushChannels(channels1.data, teams.data?.[1]?.id ?? null);
        pushChannels(channels2.data, teams.data?.[2]?.id ?? null);
        for (const d of dms.data ?? []) {
            list.push({
                kind: "dm",
                chatId: d.id,
                label: `DM · ${d.participantA === d.participantB ? "self" : "1:1"}`
            });
        }
        for (const g of groups.data ?? []) {
            list.push({
                kind: "conversation",
                conversationId: g.id,
                label: `Group · ${g.name ?? "Untitled group"}`
            });
        }
        return list;
    }, [channels0.data, channels1.data, channels2.data, dms.data, groups.data, teams.data]);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return destinations;
        return destinations.filter((d) => d.label.toLowerCase().includes(q));
    }, [destinations, filter]);

    const send = useMutation({
        mutationFn: async () => {
            if (!destination) return;
            const body = buildForwardedHtml(message);
            if (destination.kind === "channel") {
                await connectApi.sendMessage(destination.channelId, body, token);
            } else if (destination.kind === "dm") {
                await connectApi.sendDirectMessage(destination.chatId, body, token);
            } else {
                await connectParityApi.sendConversationMessage(destination.conversationId, body, token);
            }
        },
        onSuccess: () => onClose(),
        onError: (e) => setError(e instanceof Error ? e.message : String(e))
    });

    return (
        <div
            role="dialog"
            aria-label="Forward message"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={(e) => { if (e.target === e.currentTarget && !send.isPending) onClose(); }}
        >
            <div className="flex w-full max-w-md flex-col rounded-lg border bg-card shadow-2xl">
                <header className="flex items-center justify-between border-b px-4 py-3">
                    <div className="flex items-center gap-2">
                        <Send className="h-4 w-4 text-primary" aria-hidden />
                        <h2 className="text-base font-semibold">Forward message</h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={send.isPending}
                        aria-label="Close"
                        className="rounded p-1 hover:bg-accent disabled:opacity-50"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </header>

                <div className="border-b px-4 py-3">
                    <label className="mb-1 block text-xs font-medium">Send to</label>
                    <div className="relative mb-2">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter channels, chats, groups"
                            className="w-full rounded-md border bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>
                    <ul role="list" className="max-h-56 overflow-auto rounded-md border">
                        {filtered.length === 0 ? (
                            <li className="p-3 text-xs text-muted-foreground">No destinations match.</li>
                        ) : (
                            filtered.map((d) => (
                                <li key={destinationKey(d)}>
                                    <button
                                        type="button"
                                        onClick={() => setDestination(d)}
                                        className={cn(
                                            "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent",
                                            destinationKey(destination) === destinationKey(d) ? "bg-primary/10 font-medium" : null
                                        )}
                                    >
                                        <DestIcon kind={d.kind} />
                                        <span className="truncate">{d.label}</span>
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                </div>

                <div className="border-b px-4 py-3 text-xs">
                    <div className="mb-1 text-muted-foreground">Preview</div>
                    <blockquote className="rounded-md border-l-4 border-primary/60 bg-muted/30 p-2 text-sm">
                        <div className="text-[10px] text-muted-foreground">
                            Forwarded from {message.authorDisplayName ?? message.authorEmail}
                            {" · "}{new Date(message.sentAt).toLocaleString()}
                        </div>
                        <div
                            className="prose prose-sm mt-1 max-w-none break-words text-foreground dark:prose-invert"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.bodyHtml) }}
                        />
                    </blockquote>
                </div>

                {error ? (
                    <p className="mx-4 mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                        {error}
                    </p>
                ) : null}

                <div className="flex justify-end gap-2 px-4 py-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={send.isPending}
                        className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => { setError(null); send.mutate(); }}
                        disabled={!destination || send.isPending}
                        className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                        {send.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        Forward
                    </button>
                </div>
            </div>
        </div>
    );
}

function DestIcon({ kind }: { kind: Destination["kind"] }) {
    if (kind === "channel") return <Hash className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />;
    if (kind === "dm") return <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />;
    return <UsersRound className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />;
}

function destinationKey(d: Destination | null): string {
    if (!d) return "";
    if (d.kind === "channel") return `c:${d.channelId}`;
    if (d.kind === "dm") return `d:${d.chatId}`;
    return `g:${d.conversationId}`;
}

function buildForwardedHtml(message: ChatMessage): string {
    const author = escape(message.authorDisplayName ?? message.authorEmail);
    const when = new Date(message.sentAt).toLocaleString();
    return [
        `<p class="text-xs text-muted-foreground"><em>Forwarded from ${author} · ${escape(when)}</em></p>`,
        `<blockquote>${message.bodyHtml}</blockquote>`
    ].join("");
}

function escape(s: string): string {
    return s
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;");
}
